import React, { useCallback, useMemo, useState } from 'react';
import { useReportViewContext } from '../report-viewer-context';
import { useToggleTestSwitchMutation, useFastFillToggleMutation } from '@app.raytd.com/store';
import { ReportTestItem } from '@/app/features/reports/utils/entities';

type ToggleSwitchName = 'include' | 'fast_fill_accepted';

type ToggleContextType = {
    toggleSwitch: (switchType: ToggleSwitchName, item: ReportTestItem) => Promise<void>;
    isLoading: boolean;
    isFastFillLoading: boolean;
};

export const ToggleContext = React.createContext<ToggleContextType | undefined>(undefined);

export const useToggleProvider = () => {
    const context = React.useContext(ToggleContext);
    if (!context) {
        throw new Error('useToggle must be used within a ToggleProvider');
    }
    return context;
};

// ToggleProvider component
export const ToggleProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { report } = useReportViewContext();
    const [toggleSwitchMutation, { isLoading: isToggleLoading }] = useToggleTestSwitchMutation();
    const [fastFillToggleMutation, { isLoading: isFastFillLoading }] = useFastFillToggleMutation();

    const handleToggle = useCallback(async (switchType: ToggleSwitchName, item: ReportTestItem) => {

        try {
            if (switchType === 'fast_fill_accepted') {
                // Use new dedicated fast fill endpoint
                await fastFillToggleMutation({
                    testItemId: item.assessmentTestId.toString()
                }).unwrap();
            } else if (switchType === 'include' && report?.id) {
                // Only handle include toggle if we have a report context
                await toggleSwitchMutation({
                    reportId: report.id,
                    revisionId: item.revision ? item.revision.report_revision_id.toString() : report?.revisions[0]?.id?.toString(),
                    testItemId: item.assessmentTestId.toString(),
                    switchName: switchType as ToggleSwitchName
                }).unwrap();
            }
        } catch (error) {
            console.error('Failed to toggle switch:', error);
            throw error;
        }
    }, [toggleSwitchMutation, fastFillToggleMutation, report?.id, report?.revisions]);

    const value = useMemo(() => ({
        toggleSwitch: handleToggle,
        isLoading: isToggleLoading || isFastFillLoading,
        isFastFillLoading: isFastFillLoading,
    }), [isToggleLoading, isFastFillLoading]);

    return (
        <ToggleContext.Provider value={value}>
            {children}
        </ToggleContext.Provider>
    );
};
